import { ThemeToggle } from "@/components/theme-switcher";
import { useMotionVariants } from "@/components/ui/motion";
import { ProjectSwitcher } from "@/features/projects/switcher";
import { cn } from "@/lib/cn";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Boxes,
  ClipboardList,
  Clock,
  Cpu,
  Database,
  KeyRound,
  LayoutDashboard,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";

const projectNav = [
  { to: "/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/datasets", label: "Datasets", icon: Database },
  { to: "/recipes", label: "Recipes", icon: ClipboardList },
  { to: "/train", label: "Train", icon: Cpu },
  { to: "/models", label: "Models", icon: Boxes },
  { to: "/inference", label: "Inference", icon: Zap },
  { to: "/jobs", label: "Jobs", icon: Clock },
  { to: "/api-keys", label: "API Keys", icon: KeyRound },
] as const;

function NavLink({
  to,
  label,
  icon: Icon,
}: { to: string; label: string; icon: typeof LayoutDashboard }) {
  return (
    <Link
      to={to}
      className={cn(
        "group relative flex items-center gap-[11px] rounded-[9px] px-3 py-2 text-[13px] text-[#5B6475] dark:text-[#94A3BD] transition-colors",
        "hover:bg-[rgba(11,20,38,0.035)] hover:text-navy-700 dark:hover:bg-[rgba(255,255,255,0.05)] dark:hover:text-zinc-100",
        "[&.active]:bg-[linear-gradient(90deg,rgba(45,170,158,0.14),rgba(45,170,158,0.03))] [&.active]:font-semibold [&.active]:text-navy-700 dark:[&.active]:text-zinc-100",
      )}
      activeProps={{ className: "active" }}
    >
      <span className="absolute left-0 top-[7px] bottom-[7px] hidden w-[3px] rounded-[3px] bg-teal-500 shadow-[0_0_8px_rgba(45,170,158,0.55)] group-[.active]:block" />
      <Icon
        size={14}
        strokeWidth={1.75}
        className="text-[#9AA3B4] dark:text-[#5C6B85] group-[.active]:text-teal-700 dark:group-[.active]:text-teal-300"
      />
      {label}
    </Link>
  );
}

export function Shell({ children, projectName }: { children: ReactNode; projectName?: string }) {
  const { staggerContainer, fadeSlideUp } = useMotionVariants();
  return (
    <div className="hidden min-h-screen grid-cols-[228px_1fr] md:grid">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-teal-500 focus:px-3 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>
      <aside className="sticky top-0 flex h-screen flex-col border-r border-line bg-side">
        {/* Brand */}
        <div className="flex items-center gap-[10px] border-b border-line px-[18px] py-4">
          <div className="h-6 w-6 rounded-[7px] bg-gradient-to-br from-teal-500 to-teal-300 shadow-[0_4px_12px_rgba(45,170,158,0.3)]" />
          <span className="text-[15px] font-bold tracking-[-0.03em] text-navy-700 dark:text-zinc-100">
            Autolab
          </span>
        </div>

        {/* Project switcher */}
        <div className="mx-[14px] mt-[14px] mb-2">
          <ProjectSwitcher />
        </div>

        {/* Nav */}
        <nav className="flex flex-col gap-0.5 px-[10px] py-2">
          {projectName ? (
            projectNav.map((n) => <NavLink key={n.to} {...n} />)
          ) : (
            <div className="px-3 py-3 text-[11px] text-muted">
              Pick a project above to get started.
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between border-t border-line px-[18px] py-[14px]">
          <span className="font-mono text-[11px] font-medium text-muted">v2.4.0</span>
          <ThemeToggle />
        </div>
      </aside>

      <motion.main
        id="main-content"
        tabIndex={-1}
        className="relative bg-bg focus:outline-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(11,20,38,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(11,20,38,0.03) 1px,transparent 1px)",
          backgroundSize: "32px 32px",
        }}
        variants={staggerContainer}
        initial="hidden"
        animate="show"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[200px] bg-[radial-gradient(80%_100%_at_75%_0%,rgba(45,170,158,0.08),transparent_65%)]" />
        <motion.div
          variants={fadeSlideUp}
          className="relative mx-auto max-w-[1440px] px-7 py-7 pb-16"
        >
          {children}
        </motion.div>
      </motion.main>
    </div>
  );
}
